<template>
  <div class="experience-list">
    <div class="cards-stack">
      <div class="card main-card" :class="{ 'main-card--expanded': isExpanded }">
        <div ref="cardContent" class="card-content" :class="{ 'card-content--no-rating': !experienceRating }">
          <div class="date-range flex row">
            <div>{{ displayRangeDate }}</div>
            <span v-if="experience.etab_experiences[0].is_internal_xp">
              <img :src="extracadabraPicture" alt="Extracadabra-logo" class="card-content-logo q-ml-sm" />
            </span>
          </div>
          <div class="flex row">
            <div v-if="isOtherXp" class="qualification-item">
              {{ experience.etab_experiences[0].qualification_name }}
            </div>
            <div v-if="experienceRating" :class="isOtherXp ? `q-ml-sm rating` : `rating`">
              <span v-for="i in 5" :key="i" class="star" :class="{ 'star--filled': i <= experience.rating }">★</span>
            </div>
            <span v-if="experience.shift_number > 0" class="q-pl-sm rating-number">
              {{ $t("ETAB.PAGES.EXTRA.@EXPERIENCE_RATING_NUMBER", { number: experience.shift_number }) }}
            </span>
          </div>
          <div>{{ displayName }}</div>
          <QBtn flat round size="sm" class="expand-button" @click="toggleExpand">
            <i class="fa-solid fa-chevron-down plus-icon" :class="{ 'plus-icon--rotated': isExpanded }" />
          </QBtn>
        </div>
      </div>
      <!-- stacked cards skeleton -->
      <div class="stacked-cards" :style="{ top: stackedCardsTop }" :class="{ 'stacked-cards--expanded': isExpanded }">
        <div v-for="(shift, index) in experience.etab_experiences.slice(0, 2)" :key="index" :class="`stack-card stack-card-${index + 1}`"></div>
      </div>
      <div ref="shiftCardsContainer" class="shift-cards-container" :class="{ 'shift-cards-container--visible': isExpanded }">
        <div
          v-for="(shift, index) in experience.etab_experiences"
          v-show="isExpanded"
          :key="index"
          class="shift-card"
          :style="{
            '--delay': `${index * 0.1}s`,
            '--z-index': experience.etab_experiences.length - index,
          }"
        >
          <SingleExperienceComponent :shift="shift" :display-detail="false" :is-from-multiple="true" :is-other-xp="props.isOtherXp" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, nextTick, watch, onMounted } from "vue";
  import { DatesHelpers } from "@core/helpers";
  import SingleExperienceComponent from "./SingleExperience.component.vue";
  import { limitText, PERSONALIZED_DATES_FORMATS } from "@extracadabra/core-v2";
  import { Screen } from "quasar";
  import { useI18n } from "vue-i18n";

  const picture = require("@etab/assets/img/extracadabra-logo.png");
  const format = PERSONALIZED_DATES_FORMATS.CANDIDATE_EXPERIENCES_DATE_FORMAT;

  const props = defineProps({
    experience: {
      type: Object,
      required: true,
    },
    isOtherXp: {
      type: Boolean,
      default: false,
    },
  });
  const { t } = useI18n();
  const isExpanded = ref(false);
  const extracadabraPicture = ref(picture);
  const shiftCardsContainer = ref(null);
  const cardContent = ref(null);
  const stackedCardsTop = ref("98px");

  const experienceRating = computed(() => props.experience.rating);

  // Fonction pour mettre à jour la position des cartes empilées en fonction de la hauteur du contenu
  const updateStackedCardsPosition = () => {
    if (!cardContent.value) return;

    nextTick(() => {
      const height = cardContent.value.offsetHeight;
      stackedCardsTop.value = `${height - 16}px`; // -8px pour un petit chevauchement
    });
  };

  // Observer les changements qui pourraient affecter la hauteur
  watch(() => [props.isOtherXp, experienceRating.value], updateStackedCardsPosition);

  // Mettre à jour la position lors du montage initial
  onMounted(() => {
    updateStackedCardsPosition();
  });

  const toggleExpand = () => {
    isExpanded.value = !isExpanded.value;

    if (isExpanded.value) {
      nextTick(() => {
        if (shiftCardsContainer.value) {
          setTimeout(() => {
            shiftCardsContainer.value.scrollIntoView({
              behavior: "smooth",
              block: "nearest",
            });
          }, 50);
        }
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return DatesHelpers.getDayjs(dateString).format(format);
  };
  const limitByDevice = computed(() => (Screen.lt.sm ? 12 : 100));
  const displayNameFormatted = (etabName, etabType, limit) => {
    return `${limitText({ str: etabName, limit })} - ${etabType || ""}`;
  };

  const displayName = computed(() => {
    if (!props.experience.etab_name) return "";
    return displayNameFormatted(props.experience.etab_name, props.experience.etab_category, limitByDevice.value);
  });
  const isSameMonthAndSameYear = (startDate, endDate) => {
    const start = DatesHelpers.getDayjs(startDate);
    const end = DatesHelpers.getDayjs(endDate);
    return start.month() === end.month() && start.year() === end.year();
  };
  const displayRangeDate = computed(() => {
    if (isSameMonthAndSameYear(props.experience.start_date, props.experience.end_date)) {
      return String(formatDate(props.experience.start_date)).charAt(0).toUpperCase() + String(formatDate(props.experience.start_date)).slice(1);
    } else {
      return t("ETAB.PAGES.EXTRA.@DATE_RANGE", { start: formatDate(props.experience.start_date), end: formatDate(props.experience.end_date) });
    }
  });
</script>

<style lang="scss" scoped>
  .experience-list {
    width: 100%;
    max-width: 600px;
    position: relative;
    @media screen and (min-width: $breakpoint-sm-max) {
      min-width: 280px;
    }
  }

  .cards-stack {
    width: 100%;
    min-height: 106px;
    position: relative;
    padding-bottom: 20px;
  }

  .card {
    border-radius: 12px;
    position: relative;
    transition: all 0.3s ease;
  }

  .main-card {
    z-index: 10;
    position: relative;

    &--expanded {
      .card-content {
        background-color: #f8f8f8;
      }
    }
  }

  .qualification-item {
    min-height: 27px;
    font-weight: 600;
  }

  .card-content {
    background-color: #f5f5f5;
    border-radius: 12px;
    box-shadow: 0px 4px 4px 0px #00000040;
    padding: 18px 20px;
    position: relative;
    transition: all 0.3s ease;
    min-height: 106px;

    &--no-rating {
      min-height: auto;

      > .date-range {
        margin-bottom: 5px;
      }
    }

    &-logo {
      width: 50px;
      color: $secondary;
    }
  }

  .date-range {
    color: #6b7280;
    font-size: 14px;
    margin-bottom: 5px;
    min-height: 23px;
  }

  .rating {
    margin-bottom: 5px;
  }

  .rating-number {
    color: #f8a354;
  }

  .star {
    color: #e0e0e0;
    font-size: 16px;

    &--filled {
      color: #f8a354;
    }
  }

  .expand-button {
    position: absolute;
    bottom: 5px;
    right: 5px;
    background-color: #f8f8f8;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    z-index: 5;

    &:hover {
      background-color: #f0f0f0;
    }

    .plus-icon {
      transform: rotate(0);
      transition: transform 0.3s ease;

      &--rotated {
        transform: rotate(180deg);
      }
    }
  }

  /* Cartes d'empilement factices */
  .stacked-cards {
    position: absolute;
    left: 0;
    right: 0;
    z-index: 1;
    background-color: #ccc;
    transition: top 0.3s ease;

    &--expanded {
      .stack-card {
        opacity: 0;
      }
    }
  }

  .stack-card {
    position: absolute;
    height: 15px;
    background-color: #f5f5f5;
    border-radius: 12px;
    transition: all 0.3s ease;
    box-shadow: 0px 4px 4px 0px #00000040;

    &.stack-card-1 {
      top: 6px;
      left: 4px;
      right: 4px;
      z-index: 5;
    }

    &.stack-card-2 {
      top: 10px;
      left: 8px;
      right: 8px;
      z-index: 4;
    }
  }

  /* Shift cards styles - completely separate from main card */
  .shift-cards-container {
    margin-top: 8px;
    opacity: 0;
    max-height: 0;
    transition:
      opacity 0.3s ease,
      max-height 0.5s ease;
    overflow: hidden;

    &--visible {
      opacity: 1;
      max-height: 2000px;

      .shift-card {
        opacity: 1;
        transform: translateY(0);
      }
    }
  }

  .shift-card {
    opacity: 0;
    transform: translateY(20px);
    z-index: var(--z-index);
    margin-bottom: 12px;
    transition: all 0.3s ease var(--delay);
  }

  .shift-card-content {
    border-radius: 12px;
    box-shadow: 0px 4px 4px 0px #00000040;
    padding: 18px 20px;
    position: relative;
  }

  .shift-date-range {
    color: #6b7280;
    font-size: 16px;
    margin-bottom: 10px;
  }

  .shift-rating {
    margin-bottom: 15px;

    .shift-star {
      color: #e0e0e0;
      font-size: 26px;

      &.shift-star-filled {
        color: #f8a354;
      }
    }
  }

  .shift-position {
    font-size: 16px;
    color: #4b5563;
    margin-bottom: 5px;
  }

  .shift-contract-type {
    display: inline-block;
    color: white;
    font-size: 13px;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 4px;
    margin-top: 8px;
    background-color: $primary;
  }

  .shift-comment {
    margin-top: 12px;
    font-style: italic;
    color: #6b7280;
    font-size: 14px;
  }
</style>
